import React, { useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiPaperclip, FiInbox } from "react-icons/fi";
import { toast } from "react-toastify";
import StatusPill from "./StatusPill";
import { divergencias } from "../mockData";
import validacaoFiscalService from "../../../services/validacaoFiscalService";

const GRAVIDADE_VARIANT = { alta: "danger", media: "warning", baixa: "neutral" };
const GRAVIDADE_LABEL = { alta: "Gravidade alta", media: "Gravidade média", baixa: "Gravidade baixa" };

const DECISOES_PADRAO = [
  { id: "tratar", label: "Tratar e fechar a divergência" },
  { id: "rad", label: "Encaminhar para RAD (recolhimento pelo adquirente)" },
  { id: "ajustar", label: "Ajustar no ERP / solicitar correção na AA" },
];

function campo(valor) {
  return valor === null || valor === undefined || valor === "" ? "—" : valor;
}

export default function DetalheTab({ selectedId, competenciaId, onVoltar }) {
  const usandoReal = !!competenciaId && !!selectedId && !divergencias.some((d) => d.id === selectedId);
  const [real, setReal] = useState(null);
  const [carregando, setCarregando] = useState(usandoReal);
  const [decisao, setDecisao] = useState(DECISOES_PADRAO[0].id);
  const [justificativa, setJustificativa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const fileInputRef = useRef(null);

  const divergenciaMock = divergencias.find((d) => d.id === selectedId);

  useEffect(() => {
    if (!usandoReal) return;
    let cancelado = false;
    setCarregando(true);
    validacaoFiscalService
      .obterDivergencia(selectedId)
      .then((resp) => {
        if (!cancelado) setReal(resp?.data?.data || null);
      })
      .catch((err) => {
        console.error("[DetalheTab] Erro ao buscar divergência:", err);
        toast.error("Erro ao carregar divergência.");
      })
      .finally(() => !cancelado && setCarregando(false));
    return () => {
      cancelado = true;
    };
  }, [usandoReal, selectedId]);

  if (!usandoReal && !divergenciaMock) {
    return (
      <div className="empty-tab-state">
        <FiInbox />
        <h4>Nenhuma divergência selecionada</h4>
        <p>
          Abra a aba Divergências e clique na chave de um documento para ver a comparação entre AA, ERP e
          DF-e, e registrar a decisão de tratamento.
        </p>
      </div>
    );
  }

  if (usandoReal && carregando) {
    return <div className="empty-tab-state"><h4>Carregando divergência…</h4></div>;
  }

  if (usandoReal && !real) {
    return (
      <div className="empty-tab-state">
        <FiInbox />
        <h4>Divergência não encontrada</h4>
      </div>
    );
  }

  const handleEnviarDecisao = async () => {
    if (!justificativa) {
      toast.error("Justificativa é obrigatória.");
      return;
    }
    setEnviando(true);
    try {
      const resp = await validacaoFiscalService.decidirDivergencia(selectedId, { decisaoCodigo: decisao, justificativa });
      toast.success(resp?.data?.message || "Decisão registrada.");
      const atualizado = await validacaoFiscalService.obterDivergencia(selectedId);
      setReal(atualizado?.data?.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao registrar decisão.");
    } finally {
      setEnviando(false);
    }
  };

  const handleAnexarEvidencia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await validacaoFiscalService.anexarEvidencia(selectedId, file);
      toast.success("Evidência anexada.");
      const atualizado = await validacaoFiscalService.obterDivergencia(selectedId);
      setReal(atualizado?.data?.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao anexar evidência.");
    } finally {
      e.target.value = "";
    }
  };

  // ===== Modo mock (fallback, sem alterações de comportamento) =====
  if (!usandoReal) {
    const { detalhe } = divergenciaMock;
    return (
      <>
        <button type="button" className="detalhe-back" onClick={onVoltar}>
          <FiArrowLeft /> Fila de divergências
        </button>
        <div className="detalhe-header-row">
          <span className="detalhe-tipo-tag">{divergenciaMock.tipo}</span>
          <h3>{detalhe.titulo}</h3>
          <StatusPill variant={GRAVIDADE_VARIANT[divergenciaMock.gravidade]}>
            {GRAVIDADE_LABEL[divergenciaMock.gravidade]}
          </StatusPill>
        </div>
        <div className="detalhe-chave">
          Chave {divergenciaMock.chaveCompleta} · {divergenciaMock.tipoDocumento} · emissão {divergenciaMock.emissao}
        </div>
        <div className="comparison-cards">
          <div className="comparison-card">
            <div className="comparison-title">Apuração Assistida</div>
            <div className="comparison-line">
              Débito do fornecedor: {detalhe.aa.debitoFornecedor}
              <br />
              Situação: <strong>{detalhe.aa.situacao}</strong>
              <br />
              Crédito: <strong>{detalhe.aa.credito}</strong>
              <br />
              Apropriado: {detalhe.aa.apropriado}
            </div>
          </div>
          <div className="comparison-card">
            <div className="comparison-title">ERP do cliente</div>
            <div className="comparison-line">
              CBS destacada: {detalhe.erp.cbsDestacada}
              <br />
              Crédito escriturado: <strong>{detalhe.erp.creditoEscriturado}</strong>
              <br />
              CST / cClassTrib: {detalhe.erp.cst}
              <br />
              Conta contábil: {detalhe.erp.conta}
            </div>
          </div>
          <div className="comparison-card">
            <div className="comparison-title">DF-e (XML)</div>
            <div className="comparison-line">
              vCBS: {detalhe.xml.vCbs}
              <br />
              Autorizada em {detalhe.xml.autorizacao}
              <br />
              Eventos: {detalhe.xml.eventos}
              <br />
              Itens: {detalhe.xml.itens}
            </div>
          </div>
        </div>
        <div className="info-banner">
          <div className="banner-text">
            <strong>Leitura: </strong>
            <span>{detalhe.leitura}</span>
          </div>
        </div>
        <div className="decision-layout">
          <div className="decision-card">
            <div className="decision-title">Decisão</div>
            {detalhe.decisoes.map((op) => (
              <label className="decision-option" key={op.id}>
                <input type="radio" name={`acao-${divergenciaMock.id}`} defaultChecked={op.padrao} readOnly />
                {op.label}
              </label>
            ))}
            <label className="decision-field-label">
              Justificativa (obrigatória)
              <textarea rows={3} defaultValue={detalhe.justificativaPadrao} readOnly />
            </label>
            <div className="decision-actions">
              <button type="button" className="icon-btn primary" disabled title="Disponível com uma competência real aberta">
                Enviar para aprovação
              </button>
              <button type="button" className="icon-btn" disabled title="Disponível com uma competência real aberta">
                <FiPaperclip /> Anexar evidência
              </button>
            </div>
            <div className="decision-alcada">{detalhe.alcada}</div>
          </div>
          <div className="decision-card">
            <div className="decision-title">Base legal e trilha</div>
            <div style={{ fontSize: "0.84em", lineHeight: 1.6, opacity: 0.8 }}>{detalhe.baseLegalTexto}</div>
            <div className="timeline">
              {detalhe.trilha.map((item, i) => (
                <div className="timeline-item" key={i}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="table-footnote">
          Dados ilustrativos. Toda decisão e ajuste fica registrada com autor, data e justificativa.
        </div>
      </>
    );
  }

  // ===== Modo real =====
  const { divergencia, ajuda, fontes, historico } = real;
  const aa = fontes.aa[0] || {};
  const erp = fontes.erp[0] || {};
  const dfe = fontes.dfe[0] || {};

  return (
    <>
      <button type="button" className="detalhe-back" onClick={onVoltar}>
        <FiArrowLeft /> Fila de divergências
      </button>

      <div className="detalhe-header-row">
        <span className="detalhe-tipo-tag">{divergencia.codigo}</span>
        <h3>{ajuda?.titulo || divergencia.causa}</h3>
        <StatusPill variant={GRAVIDADE_VARIANT[divergencia.gravidade]}>
          {GRAVIDADE_LABEL[divergencia.gravidade]}
        </StatusPill>
      </div>
      <div className="detalhe-chave">
        Chave {campo(divergencia.chaveAcesso)} · status: {divergencia.status}
      </div>

      <div className="comparison-cards">
        <div className="comparison-card">
          <div className="comparison-title">Apuração Assistida</div>
          <div className="comparison-line">
            Valor CBS: {campo(aa.vCbs)}
            <br />
            Tipo: <strong>{campo(aa.tipoRegistroAA)}</strong>
            <br />
            Situação: <strong>{campo(aa.situacaoAA)}</strong>
          </div>
        </div>
        <div className="comparison-card">
          <div className="comparison-title">ERP do cliente</div>
          <div className="comparison-line">
            CBS destacada: {campo(erp.vCbs)}
            <br />
            CST / cClassTrib: {campo(erp.cst)} / {campo(erp.cClassTrib)}
            <br />
            Conta contábil: {campo(erp.contaContabil)}
          </div>
        </div>
        <div className="comparison-card">
          <div className="comparison-title">DF-e (XML)</div>
          <div className="comparison-line">
            vCBS: {campo(dfe.vCbs)}
            <br />
            Autorizada em: {campo(dfe.autorizacao)}
            <br />
            Itens: {campo(dfe.itens)}
          </div>
        </div>
      </div>

      <div className="info-banner">
        <div className="banner-text">
          <strong>Causa provável: </strong>
          <span>{divergencia.causa}</span>
        </div>
      </div>

      <div className="decision-layout">
        <div className="decision-card">
          <div className="decision-title">Decisão</div>
          {DECISOES_PADRAO.map((op) => (
            <label className="decision-option" key={op.id}>
              <input type="radio" name="acao-real" checked={decisao === op.id} onChange={() => setDecisao(op.id)} />
              {op.label}
            </label>
          ))}
          <label className="decision-field-label">
            Justificativa (obrigatória)
            <textarea rows={3} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
          </label>
          <div className="decision-actions">
            <button type="button" className="icon-btn primary" onClick={handleEnviarDecisao} disabled={enviando}>
              {enviando ? "Enviando…" : "Enviar para aprovação"}
            </button>
            <button type="button" className="icon-btn" onClick={() => fileInputRef.current?.click()}>
              <FiPaperclip /> Anexar evidência
            </button>
            <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleAnexarEvidencia} />
          </div>
          <div className="decision-alcada">Alçada verificada automaticamente pelo valor da divergência.</div>
        </div>

        <div className="decision-card">
          <div className="decision-title">Base legal e trilha</div>
          <div style={{ fontSize: "0.84em", lineHeight: 1.6, opacity: 0.8 }}>
            {(ajuda?.baseLegal || divergencia.baseLegal || []).join(" · ")}
          </div>
          <div className="timeline">
            {historico.map((item, i) => (
              <div className="timeline-item" key={i}>
                {new Date(item.momento).toLocaleString("pt-BR")} · {item.evento}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="table-footnote">Toda decisão e ajuste fica registrada com autor, data e justificativa (trilha de auditoria).</div>
    </>
  );
}
